import React, { useContext } from 'react';

// DEPENDENCIES
import { Row, Col } from 'react-bootstrap';

// GLOBAL VARIABLES
import { ENDPOINTS } from 'endpoints';
import { INTERFACE } from 'pathnames.js';
import { SORT_ORDER } from 'globals.js';
import { NUMBER_FORMATS } from 'numberFormats.js';

// CORE COMPONENTS
import Form from 'core/form/Form';

// HELPERS
import { getListData } from 'helpers/getListData';
import { ListContext } from 'helpers/getListData';

// PARTS
import Main from 'parts/main/Main';
import Title from 'parts/title/Title';
import Table, { Filters } from 'parts/table/Table';

// LOCAL VARIABLES
import {
  COLUMNS as ACCOUNTING_COLUMNS,
  FE_ROLLUP_COLUMNS,
  FE_TOTAL_COLUMNS_MATCHES,
  FE_TOTAL_COLUMNS_NO_MATCHES
} from './variables';
import { ACTIONS, COLUMNS } from 'columns';

// LOCAL COMPONENTS
import ComparisonCell from './parts/ComparisonCell';
import InfoAlert from './parts/InfoAlert';
import TotalTable from './parts/TotalTable';

// MAIN COMPONENT
const Frontend = () => {
  // CONTEXT
  const { data: { total_records = 0 } = {}, preferences: { filters } = {} } = useContext(ListContext) || {};

  const totalTableColumns = {
    matches: FE_TOTAL_COLUMNS_MATCHES,
    nomatches: FE_TOTAL_COLUMNS_NO_MATCHES
  };

  // RENDER
  return (
    <Main>
      <Title documentTitle="Accounting Tasks -> Frontend" title="Frontend" />
      <Filters>
        <Filters.Date.Range />
        <Filters.Button.SalesType />
        <Filters.Button.NewUsed />
        <Filters.Select.Match />
        <Filters.Search />
      </Filters>
      <Table
        mobilePreviewKeys={['customer1_full_name', 'sales_time', 'Vehicle']}
        className="mb-0"
        showNumber
        autoSizeHeight={false}
      />
      <Row className="flex-grow-0">
        <Col xs={{ span: 24, order: 2 }} lg={{ span: 12, order: 1 }}>
          <InfoAlert />
        </Col>
        <Col xs={{ span: 24, order: 1 }} lg={{ span: 12, order: 2 }}>
          <Row>
            <Col xs={24} xl={12} className="mb-3">
              <TotalTable
                columns={FE_ROLLUP_COLUMNS} //
                headerLabels={[null, 'Total', 'Avg']}
              />
            </Col>
            <Col xs={24} xl={12} className="mb-3 mb-lg-0">
              <TotalTable
                columns={totalTableColumns[filters?.auditStatus] || []} //
                headerLabels={[`${total_records} Total Records`, 'Total', 'Avg']}
              />
            </Col>
          </Row>
        </Col>
      </Row>
    </Main>
  );
};

// EXPORT
export default getListData(Frontend, {
  key: 'accountTaskFrontend',
  fetchArgs: {
    endpoint: ENDPOINTS.AccountingTasks.FE_gross.list
  },
  defaults: {
    startDate: Form.Date.OPTIONS.thisMonth.startDate,
    endDate: Form.Date.OPTIONS.thisMonth.endDate,
    auditStatus: 'nomatches',
    salesType: 'R',
    sort: 'sales_time',
    order: SORT_ORDER.ascending
  },
  columns: [
    {
      ...COLUMNS.deliveredDate,
      key: 'sales_time'
    },
    COLUMNS.customer(),
    COLUMNS.salesPerson,
    {
      ...COLUMNS.fmManager,
      key: 'fm_assignment_user_full_name'
    },
    {
      ...COLUMNS.makeModel,
      key: 'Vehicle'
    },
    {
      ...ACCOUNTING_COLUMNS.vinNumber,
      comparison: {
        highlight: false
      }
    },
    {
      ...ACCOUNTING_COLUMNS.stockNumber,
      comparison: {
        highlight: false
      }
    },
    {
      ...ACCOUNTING_COLUMNS.dealNumber,
      comparison: {
        highlight: false
      }
    },
    {
      ...COLUMNS.frontendGrossBP,
      key: 'frontend_gross_bp',
      numberFormat: NUMBER_FORMATS.dollars,
      component: ComparisonCell,
      comparison: {
        comparedToKey: 'audit_frontend_gross_bp',
        tooltip: ['FE Gross BP', 'DMS FE Gross BP']
      },
      width: 110
    },
    {
      ...COLUMNS.dealerPack,
      key: 'dealer_pack'
    },
    {
      ...COLUMNS.frontendGrossAP,
      key: 'frontend_gross_ap'
    }
  ],
  actions: [ACTIONS.open(INTERFACE.sales, 'sales_id')],
  sortable: true
});
